import { named, toggle } from '../../themes/settingFields';
import { element, only } from './definition';
import { look, paint, typeface } from './styleTokens';

// The parts of a collection or search page around its product grid: the filters, the count and sort, and
// the pages. Each draws the page's one listing read (`ListingContext`), so a merchant can place them as
// Sleek does — the filters down the start edge, the count and sort over the grid, the pages under it — or
// leave any out. They are offered only on the pages that have a listing; anywhere else they draw nothing.

/** The pages that have a listing. */
const LISTING_PAGES = only(['collection', 'search']);

/** The filters: availability, price, the options, the types and the brands, each a fold of its own. */
export const listingFiltersElement = element('listing-filters', {
  label: 'Filters',
  description: 'Lets shoppers narrow the products to what is in stock, a price, an option, a type or a brand. A drawer on phones.',
  category: 'Other',
  icon: 'filter',
  binds: LISTING_PAGES,
  props: [
    toggle('showAvailability', 'In stock'),
    toggle('showPrice', 'Price'),
    { ...toggle('showOptions', 'Options'), help: 'One filter per option your products have, such as size or colour.' },
    toggle('showTypes', 'Product type'),
    toggle('showVendors', 'Brand'),
    { ...toggle('startOpen', 'Open at first'), help: 'Off, each filter shows only its name until a shopper opens it.' },
    named('layout', 'Layout', [['side', 'A column beside the products'], ['bar', 'A row of dropdowns above them']])
  ],
  defaults: {
    props: { showAvailability: true, showPrice: true, showOptions: true, showTypes: true, showVendors: false, startOpen: true },
    styles: look({ width: '100%', fontFamily: typeface('body'), color: paint('heading') })
  }
});

/** The bar over the grid: how many products, the sort, and the filters in use as chips to take off. */
export const listingToolbarElement = element('listing-toolbar', {
  label: 'Count and sort',
  description: 'How many products there are, a sort menu, and the filters in use, each removable.',
  category: 'Other',
  icon: 'list',
  binds: LISTING_PAGES,
  props: [toggle('showSort', 'Sort menu'), named('sortLook', 'Sort menu look', [['plain', 'Words only'], ['box', 'In an outlined box']]), toggle('showChips', 'Filters in use')],
  defaults: {
    props: { showSort: true, sortLook: 'plain', showChips: true },
    styles: look({ width: '100%', fontFamily: typeface('body'), color: paint('heading') })
  }
});

/** The page numbers under the grid. */
export const listingPaginationElement = element('listing-pagination', {
  label: 'Pages',
  description: 'Numbered pages under the products, shown once there is more than one.',
  category: 'Other',
  icon: 'arrow-right',
  binds: LISTING_PAGES,
  props: [],
  defaults: { props: {}, styles: look({ width: '100%', fontFamily: typeface('body'), color: paint('heading') }) }
});
